import { ref, computed } from 'vue'
import { getFeatureFlags } from '@/config/featureFlags'

// Get feature flags from configuration
const FEATURE_FLAGS = getFeatureFlags()

// Runtime feature flags (can be toggled without rebuild)
const runtimeFlags = ref({
  // Add any runtime-configurable flags here
  // These can be controlled via environment variables or API calls
})

/**
 * Feature flags composable
 * Provides easy access to feature flags throughout the application
 */
export function useFeatureFlags() {
  // Get all feature flags
  const getAllFlags = () => ({ ...FEATURE_FLAGS, ...runtimeFlags.value })
  
  // Check if a specific feature is enabled
  const isEnabled = (flagName) => {
    return FEATURE_FLAGS[flagName] !== false && runtimeFlags.value[flagName] !== false
  }
  
  // Check if multiple features are enabled (all must be true)
  const areAllEnabled = (...flagNames) => {
    return flagNames.every(flagName => isEnabled(flagName))
  }
  
  // Check if any of the features are enabled (at least one must be true)
  const isAnyEnabled = (...flagNames) => {
    return flagNames.some(flagName => isEnabled(flagName))
  }
  
  // Get disabled features (useful for debugging)
  const getDisabledFeatures = () => {
    const allFlags = getAllFlags()
    return Object.entries(allFlags)
      .filter(([, enabled]) => !enabled)
      .map(([name]) => name)
  }
  
  // Get enabled features (useful for debugging)
  const getEnabledFeatures = () => {
    const allFlags = getAllFlags()
    return Object.entries(allFlags)
      .filter(([, enabled]) => enabled)
      .map(([name]) => name)
  }
  
  // Toggle a runtime flag (for development/testing)
  const toggleRuntimeFlag = (flagName, value) => {
    runtimeFlags.value[flagName] = value
  }
  
  // Computed properties for common feature combinations
  const navigationFeatures = computed(() => ({
    adminPanel: isEnabled('ADMIN_PANEL'),
    departmentsView: isEnabled('DEPARTMENTS_VIEW'),
    howToPlay: isEnabled('HOW_TO_PLAY'),
    setPassword: isEnabled('SET_PASSWORD'),
  }))
  
  const gameFeatures = computed(() => ({
    timer: isEnabled('GAME_TIMER'),
    notes: isEnabled('GAME_NOTES'),
    summary: isEnabled('GAME_SUMMARY'),
    fontSizeAdjuster: isEnabled('FONT_SIZE_ADJUSTER'),
  }))
  
  const cardManagementFeatures = computed(() => ({
    editing: isEnabled('CARD_EDITING'),
    deletion: isEnabled('CARD_DELETION'),
    bulkOperations: isEnabled('CARD_BULK_OPERATIONS'),
    images: isEnabled('CARD_IMAGES'),
  }))
  
  const authFeatures = computed(() => ({
    adminAuth: isEnabled('ADMIN_AUTH'),
    gameAuth: isEnabled('GAME_AUTH'),
  }))
  
  return {
    // Core functions
    isEnabled,
    areAllEnabled,
    isAnyEnabled,
    getAllFlags,
    getDisabledFeatures,
    getEnabledFeatures,
    toggleRuntimeFlag,
    
    // Computed feature groups
    navigationFeatures,
    gameFeatures,
    cardManagementFeatures,
    authFeatures,
    
    // Individual flags (for convenience)
    flags: FEATURE_FLAGS,
    runtimeFlags: runtimeFlags.value,
  }
}

// Export individual flags for direct import
export const featureFlags = FEATURE_FLAGS

// Export the composable as default
export default useFeatureFlags
